import { useEffect, useRef, useState } from "react";
import styles from "./TopUpBalanceModal.module.scss";
import { TopUpView } from "@/modules/Balance/ui/TopUpView";
import { cn } from "@/shared/lib/utils";
import { useGetTopUpInfo } from "@/shared/hooks/useGetTopUpInfo";
import { useBalance } from "@/shared/hooks/useBalance";
import { useSuppliersStore } from "@/shared/stores/suppliersStore";
import { formatCurrency } from "@/shared/lib/formatters";
import viewStyles from "@/modules/Balance/ui/TopUpView.module.scss";
import { useQueryClient } from "@tanstack/react-query";
import { SuccessIcon } from "./assets/SuccessIcon";
import { ErrorIcon } from "./assets/ErrorIcon";

type View =
  | "default"
  | "loading"
  | "success-card"
  | "error-topup"
  | "error-card-exists"
  | "success-topup";

interface TopUpBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialState?: View;
}

export function TopUpBalanceModal({
  isOpen,
  onClose,
  initialState = "default",
}: TopUpBalanceModalProps) {
  const [view, setView] = useState<View>(initialState);
  const [pollingTopUpId, setPollingTopUpId] = useState<string | null>(null);
  const [creditedAmount, setCreditedAmount] = useState<number | null>(null);
  const startedOutsideRef = useRef(false);

  const { selectedSupplierId } = useSuppliersStore();
  const { balance } = useBalance(selectedSupplierId);
  const queryClient = useQueryClient();

  const { data: topUpInfo, isError: isTopUpInfoError } = useGetTopUpInfo({
    topUpId: pollingTopUpId,
    enabled: !!pollingTopUpId,
  });

  useEffect(() => {
    if (topUpInfo?.status === "succeeded") {
      if (selectedSupplierId) {
        queryClient.invalidateQueries({
          queryKey: ["balance", selectedSupplierId],
        });
      }
      setView("success-topup");
    } else if (topUpInfo?.status === "cancelled" || isTopUpInfoError) {
      setPollingTopUpId(null);
      setView("error-topup");
    }
  }, [topUpInfo, isTopUpInfoError, queryClient, selectedSupplierId]);

  useEffect(() => {
    if (isOpen) {
      setView(initialState);
      setPollingTopUpId(null);
      setCreditedAmount(null);
    }
  }, [isOpen, initialState]);

  if (!isOpen) return null;

  const handleTopUpSuccess = (topUpId: string, totalWithBonus: number) => {
    setPollingTopUpId(topUpId);
    setCreditedAmount(totalWithBonus);
    setView("loading");
  };

  const renderContent = () => {
    switch (view) {
      case "loading":
        return (
          <div
            className={cn(styles.modal, styles.loadingModal)}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.spinner}></div>
            <div>Ожидаем подтверждения от платежной системы...</div>
          </div>
        );
      case "success-card":
        return (
          <div
            className={cn(styles.modal)}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.iconContainer}>
              <SuccessIcon />
            </div>
            <h2 className={viewStyles.title}>Метод оплаты успешно добавлен</h2>
            <div className={viewStyles.buttons}>
              <button
                onClick={() => setView("default")}
                className={viewStyles.confirmButton}
              >
                Продолжить
              </button>
            </div>
          </div>
        );
      case "error-topup":
        return (
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.iconContainer}>
              <ErrorIcon />
            </div>
            <p className={styles.message}>Не удалось завершить операцию.</p>
            <div className={viewStyles.buttons}>
              <button
                onClick={() => setView("default")}
                className={viewStyles.confirmButton}
              >
                Попробовать снова
              </button>
            </div>
          </div>
        );
      case "error-card-exists":
        return (
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.iconContainer}>
              <ErrorIcon />
            </div>
            <p className={styles.message}>Эта карта уже привязана.</p>
            <div className={viewStyles.buttons}>
              <button
                onClick={onClose}
                className={viewStyles.confirmButton}
              >
                Понятно
              </button>
            </div>
          </div>
        );
      case "success-topup":
        return (
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.iconContainer}>
              <SuccessIcon />
            </div>
            <p className={styles.message}>Баланс успешно пополнен.</p>

            <div className={styles.successInfo}>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Зачислено:</span>
                <span className={styles.infoValue}>
                  {creditedAmount
                    ? formatCurrency(creditedAmount)
                    : topUpInfo?.amount
                      ? formatCurrency(topUpInfo.amount / 100)
                      : "-"}
                </span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Текущий баланс:</span>
                <span className={styles.infoValue}>
                  {balance?.amount ? formatCurrency(balance.amount / 100) : "-"}
                </span>
              </div>
            </div>

            <div className={viewStyles.buttons}>
              <button onClick={onClose} className={viewStyles.confirmButton}>
                Закрыть
              </button>
            </div>
          </div>
        );
      case "default":
      default:
        return (
          <TopUpView onClose={onClose} onTopUpSuccess={handleTopUpSuccess} />
        );
    }
  };

  return (
    <div
      className={cn(styles.overlay, {
        [styles.infoModal]: view !== "default",
      })}
      onMouseDown={(e) => {
        startedOutsideRef.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        if (startedOutsideRef.current && e.target === e.currentTarget) {
          onClose();
        }

        startedOutsideRef.current = false;
      }}
    >
      {renderContent()}
    </div>
  );
}
